/* Nationwide CareMatters Insurance Application */
import { sharedOneAmericaQuestions } from './oneAmericaAppSharedQuestions';
import { sharedOneAmericaSections } from './oneAmericaAppSharedSections';
import { oneAmericaSharedDefs } from './oneAmericaSharedDefs';
import { oneAmericaPreApplicationFormDefinition } from '@shared/policy-application/oneAmericaPreApplicationFormDef';
import { FundingPolicyType } from '@shared';

const questions: Record<string, PolicyFormQuestion> = {
  ...oneAmericaPreApplicationFormDefinition.questions,
  ...sharedOneAmericaQuestions,

  /* Section 6 - Policy Selections */
  policyNonforfeitureBenefit: {
    id: 'policy-nonforfeiture-benefit',
    questionRef: 'policyNonforfeitureBenefit',
    title: 'Nonforfeiture Benefit',
    type: 'radio',
    required: true,
    options: [
      { id: 'yes', value: 'Yes' },
      { id: 'no', value: 'No' },
    ],
  },

  /* Section 13 - Premium (Annual/Single) (Rider) */
  ltcPremiumForNonforfeitureBenefitRider: {
    id: 'ltc-premium-for-nonforfeiture-benefit-rider',
    questionRef: 'ltcPremiumForNonforfeitureBenefitRider',
    title: 'Premium for Nonforfeiture Benefit Rider',
    type: 'text',
    placeholder: '$',
    validationType: 'currency',
  },

  /* Important Notice Regarding Replacement Of Life Insurance Or Annuities */
  replacementCompanySelection: {
    id: 'replacement-company-selection',
    questionRef: 'replacementCompanySelection',
    title: 'Check all that apply',
    type: 'multiselect',
    required: true,
    options: [
      { id: 'aul', value: 'American United Life Insurance Company® (AUL)' },
      { id: 'state-life', value: 'The State Life Insurance Company (State Life)' },
      { id: 'pml', value: 'Pioneer Mutual Life Insurance Company (PML)' },
    ],
  },
  replacementExistingInsurance: {
    id: 'replacement-existing-insurance',
    questionRef: 'replacementExistingInsurance',
    title: 'Do you have existing life insurance or annuity(ies) with this or any other company?',
    type: 'radio',
    required: true,
    options: [
      { id: 'yes', value: 'Yes' },
      { id: 'no', value: 'No' },
    ],
  },
  replacementDiscontinuingPolicy: {
    id: 'replacement-discontinuing-policy',
    questionRef: 'replacementDiscontinuingPolicy',
    title:
      'Are you considering discontinuing making premium payments, surrendering, forfeiting, assigning to the insurer, or otherwise terminating your existing policy or contract?',
    type: 'radio',
    required: true,
    options: [
      { id: 'yes', value: 'Yes' },
      { id: 'no', value: 'No' },
    ],
  },
  replacementUsingFunds: {
    id: 'replacement-using-funds',
    questionRef: 'replacementUsingFunds',
    title:
      'Are you considering using funds from your existing policies or contracts to pay premiums due on the new policy or contract?',
    type: 'radio',
    required: true,
    options: [
      { id: 'yes', value: 'Yes' },
      { id: 'no', value: 'No' },
    ],
  },
  replacementExistingPolicies: {
    id: 'replacement-existing-policies',
    questionRef: 'replacementExistingPolicies',
    title: 'Existing Policy or Contract Information',
    type: 'table',
    required: false,
    minRows: 0,
    maxRows: 3,
    columns: [
      {
        id: 'insurerName',
        title: 'Insurer Name',
        type: 'text',
        required: false,
      },
      {
        id: 'contractPolicyNumber',
        title: 'Contract or Policy #',
        type: 'text',
        required: false,
      },
      {
        id: 'insuredAnnuitant',
        title: 'Insured or Annuitant',
        type: 'text',
        required: false,
      },
      {
        id: 'replacedOrFinancing',
        title: 'Replaced (R) or Financing (F)',
        type: 'select',
        required: false,
        options: [
          { id: 'R', value: 'R' },
          { id: 'F', value: 'F' },
        ],
      },
    ],
    conditional: {
      visible: {
        operator: 'or',
        conditions: [
          {
            field: 'replacementDiscontinuingPolicy',
            value: 'yes',
            operator: 'equals',
          },
          {
            field: 'replacementUsingFunds',
            value: 'yes',
            operator: 'equals',
          },
        ],
      },
    },
  },
  replacementReason: {
    id: 'replacement-reason',
    questionRef: 'replacementReason',
    title: 'The existing policy or contract is being replaced because',
    type: 'textarea',
    required: false,
    conditional: {
      visible: {
        field: 'replacementDiscontinuingPolicy',
        value: 'yes',
        operator: 'equals',
      },
    },
  },
  replacementProducerName: {
    id: 'replacement-producer-name',
    questionRef: 'replacementProducerName',
    title: 'Print name of Producer(s)',
    type: 'text',
    required: true,
  },
  replacementProducerAddress: {
    id: 'replacement-producer-address',
    questionRef: 'replacementProducerAddress',
    title: 'Printed Address of Producer',
    type: 'text',
    required: true,
  },
  replacementDoNotWantNotice: {
    id: 'replacement-do-not-want-notice',
    questionRef: 'replacementDoNotWantNotice',
    title: 'Do you want this notice read aloud to {clientFullName}?',
    type: 'radio',
    required: true,
    options: [
      { id: 'yes', value: 'Yes' },
      { id: 'no', value: 'No' },
    ],
  },
  replacementDoNotWantNoticeSecondaryInsured: {
    id: 'replacement-do-not-want-notice-secondary-insured',
    questionRef: 'replacementDoNotWantNoticeSecondaryInsured',
    title: 'Do you want this notice read aloud to {clientPartnerFullName}?',
    type: 'radio',
    required: true,
    options: [
      { id: 'yes', value: 'Yes' },
      { id: 'no', value: 'No' },
    ],
    conditional: {
      visible: {
        field: 'fundingPolicyType',
        operator: 'equals' as const,
        value: FundingPolicyType.jointAssetCare,
      },
    },
  },

  /* Long-Term Care Insurance Personal Worksheet */
  ltcResourcesForPremium: {
    id: 'ltc-resources-for-premium',
    questionRef: 'ltcResourcesForPremium',
    title: 'What resources will you use to pay your premium?',
    type: 'multiselect',
    required: false,
    options: [
      { id: 'current-income-employment', value: 'Current Income From Employment' },
      { id: 'savings', value: 'Savings' },
      { id: 'money-from-family', value: 'Money From My Family' },
      { id: 'current-income-investments', value: 'Current Income From Investments' },
      { id: 'sell-investments', value: 'Sell Investments' },
      { id: 'other', value: 'Other' },
      { id: 'current-income', value: 'Other Current Income' },
      { id: 'sell-assets', value: 'Sell Other Assets' },
    ],
  },
  ltcOtherResourceSpecify: {
    id: 'ltc-other-resource-specify',
    questionRef: 'ltcOtherResourceSpecify',
    title: 'Other (Please specify)',
    type: 'text',
    required: false,
    conditional: {
      visible: {
        field: 'ltcResourcesForPremium',
        value: 'other',
        operator: 'contains',
      },
    },
  },
  ltcAffordIfSpouseDies: {
    id: 'ltc-afford-if-spouse-dies',
    questionRef: 'ltcAffordIfSpouseDies',
    title: 'Could you afford to keep this policy/rider if your spouse or partner dies first?',
    type: 'radio',
    required: false,
    options: [
      { id: 'yes', value: 'Yes' },
      { id: 'no', value: 'No' },
      { id: 'had-not-thought', value: 'Had Not Thought About It' },
      { id: 'do-not-know', value: 'Do Not Know' },
      { id: 'does-not-apply', value: 'Does Not Apply' },
    ],
  },
  ltcIncomeChangeAffectPremium: {
    id: 'ltc-income-change-affect-premium',
    questionRef: 'ltcIncomeChangeAffectPremium',
    title:
      'If you plan to pay premiums from your income, have you thought about how a change in your income would affect your ability to continue to pay the premium?',
    type: 'radio',
    required: false,
    options: [
      { id: 'yes', value: 'Yes' },
      { id: 'no', value: 'No' },
      { id: 'do-not-know', value: 'Do Not Know' },
    ],
  },
  ltcPayForFutureCosts: {
    id: 'ltc-pay-for-future-costs',
    questionRef: 'ltcPayForFutureCosts',
    title:
      'If you do not buy inflation protection, how will you pay for the differences between future costs and your daily benefit amount?',
    type: 'multiselect',
    required: false,
    options: [
      { id: 'from-income', value: 'From My Income' },
      { id: 'from-savings', value: 'From Savings' },
      { id: 'from-investments', value: 'From Investments' },
      { id: 'sell-assets', value: 'Sell Other Assets' },
      { id: 'money-from-family', value: 'Money From My Family' },
      { id: 'other', value: 'Other' },
    ],
    conditional: {
      visible: {
        field: 'ltcBuyInflationProtection',
        value: 'no',
        operator: 'equals',
      },
    },
  },
  ltcPayForFutureCostsOther: {
    id: 'ltc-pay-for-future-costs-other',
    questionRef: 'ltcPayForFutureCostsOther',
    title: 'Other (Please specify)',
    type: 'text',
    required: false,
    conditional: {
      visible: {
        field: 'ltcPayForFutureCosts',
        value: 'other',
        operator: 'contains',
      },
    },
  },

  /* Medical Questions A - During past 10 years */
  medicalAidsFirstInsured: {
    id: 'medical-aids-first-insured',
    questionRef: 'medicalAidsFirstInsured',
    title:
      'AIDS (acquired immunodeficiency syndrome), ARC (AIDS-related complex), or any other disorder of the immune system?',
    type: 'radio',
    required: true,
    options: [
      { id: 'yes', value: 'Yes' },
      { id: 'no', value: 'No' },
    ],
  },
  medicalAidsSecondInsured: {
    id: 'medical-aids-second-insured',
    questionRef: 'medicalAidsSecondInsured',
    title:
      'AIDS (acquired immunodeficiency syndrome), ARC (AIDS-related complex), or any other disorder of the immune system?',
    type: 'radio',
    required: true,
    options: [
      { id: 'yes', value: 'Yes' },
      { id: 'no', value: 'No' },
    ],
  },
  medicalDiagnosticTestsFirstInsured: {
    id: 'medical-diagnostic-tests-first-insured',
    questionRef: 'medicalDiagnosticTestsFirstInsured',
    title:
      'Been advised by a member of the medical profession to have any diagnostic tests, other than tests related to the Human Immunodeficiency Virus (AIDS Virus), hospitalization, or surgery which has not been completed?',
    type: 'radio',
    required: true,
    options: [
      { id: 'yes', value: 'Yes' },
      { id: 'no', value: 'No' },
    ],
  },
  medicalDiagnosticTestsSecondInsured: {
    id: 'medical-diagnostic-tests-second-insured',
    questionRef: 'medicalDiagnosticTestsSecondInsured',
    title:
      'Been advised by a member of the medical profession to have any diagnostic tests, other than tests related to the Human Immunodeficiency Virus (AIDS Virus), hospitalization, or surgery which has not been completed?',
    type: 'radio',
    required: true,
    options: [
      { id: 'yes', value: 'Yes' },
      { id: 'no', value: 'No' },
    ],
  },

  /* Representative's Statement */
  representativeAgentInfo: {
    id: 'representative-agent-info',
    questionRef: 'representativeAgentInfo',
    title: 'Representative Information',
    type: 'table',
    required: false,
    minRows: 1,
    maxRows: 3,
    columns: [
      {
        id: 'name',
        title: 'Name of Representative',
        type: 'text',
        required: true,
      },
      {
        id: 'email',
        title: 'Email',
        type: 'text',
        required: true,
      },
      {
        id: 'percentage',
        title: '%',
        type: 'number',
        required: true,
      },
      {
        id: 'code',
        title: "Representative's SL Code",
        type: 'text',
        required: true,
      },
    ],
  },
};

export const oneAmericaAssetCareFormDef: PolicyFormDefinition = {
  requiredParameters: oneAmericaSharedDefs.requiredParameters,
  sections: [
    ...oneAmericaPreApplicationFormDefinition.sections,
    sharedOneAmericaSections.firstProposedInsured,
    sharedOneAmericaSections.secondProposedInsured,
    sharedOneAmericaSections.owner,
    sharedOneAmericaSections.jointOwner,
    sharedOneAmericaSections.beneficiary,
    sharedOneAmericaSections.policySelections,
    sharedOneAmericaSections.annualIncome,
    sharedOneAmericaSections.protectionAgainstLapse,
    /* 9 ▸ Information Regarding Other Coverage ------------------------------------------------------- */
    {
      id: 'other-coverage',
      title: 'Information Regarding Other Coverage',
      subsections: [
        {
          id: 'ltc-coverage',
          title: 'Information Regarding Other Coverage',
          questionGroups: [
            {
              id: 'current-ltc',
              format: 'default',
              questions: ['coverageHasOtherLtc', 'coverageHadLtcLastYear'],
            },
            {
              id: 'ltc-details',
              format: 'grid',
              questions: ['coverageLtcCompanyName', 'coverageLtcLapseDate'],
              conditional: {
                visible: {
                  field: 'coverageHadLtcLastYear',
                  operator: 'equals',
                  value: 'yes',
                },
              },
            },
            {
              id: 'insurance-status',
              format: 'default',
              questions: ['coverageHasExistingInsurance'],
            },
            {
              id: 'insurance-details',
              format: 'default',
              questions: ['coverageExistingInsuranceDetails'],
              conditional: {
                visible: {
                  operator: 'or',
                  conditions: [
                    {
                      field: 'coverageHasOtherLtc',
                      operator: 'equals',
                      value: 'yes',
                    },
                    {
                      field: 'coverageHadLtcLastYear',
                      operator: 'equals',
                      value: 'yes',
                    },
                    {
                      field: 'coverageHasExistingInsurance',
                      operator: 'equals',
                      value: 'yes',
                    },
                  ],
                },
              },
            },
            {
              id: 'medicaid-status',
              format: 'default',
              questions: ['coverageEligibleMedicaid'],
            },
            {
              id: 'replacement-info',
              format: 'default',
              questions: ['coverageReplacingPolicy'],
            },
            {
              id: 'pending-status',
              format: 'default',
              questions: ['coverageApplicationPending'],
            },
            {
              id: 'pending-details',
              format: 'grid',
              questions: ['coveragePendingCompany', 'coveragePendingAmount'],
              conditional: {
                visible: {
                  field: 'coverageApplicationPending',
                  operator: 'equals',
                  value: 'yes',
                },
              },
            },
            {
              id: 'settlement-status',
              format: 'default',
              questions: ['coverageSoldLifeSettlement'],
            },
            {
              id: 'settlement-details',
              format: 'grid',
              questions: ['coverageSettlementCompany', 'coverageSettlementAmount'],
              conditional: {
                visible: {
                  field: 'coverageSoldLifeSettlement',
                  operator: 'equals',
                  value: 'yes',
                },
              },
            },
          ],
        },
      ],
    },
    sharedOneAmericaSections.specialRequest,
    sharedOneAmericaSections.acknowledgement,
    sharedOneAmericaSections.substituteW9,
    /* 13 ▸ Representative's Statement ------------------------------------------------------- */
    {
      id: 'representative-statement',
      title: "Representative's Statement",
      subsections: [
        {
          id: 'replacement-knowledge',
          title: 'Replacement Information',
          questionGroups: [
            {
              id: 'replacement-status',
              format: 'default',
              questions: ['representativeReplacementKnowledge'],
            },
          ],
        },
        {
          id: 'identification-info',
          title: 'Identification Information',
          questionGroups: [
            {
              id: 'witness-identification',
              format: 'grid',
              questions: [
                'representativeWitnessedSignatures',
                'representativeIdentificationMethod',
              ],
            },
            {
              id: 'relationship-details',
              format: 'default',
              questions: ['representativeRelationshipExplanation', 'representativeFormerNames'],
            },
          ],
        },
        {
          id: 'evaluation-info',
          title: 'Application Evaluation',
          questionGroups: [
            {
              id: 'other-applications',
              format: 'default',
              questions: ['representativeEvaluateWithOthers'],
            },
            {
              id: 'other-applicant-details',
              format: 'grid',
              questions: [
                'representativeOtherApplicantName',
                'representativeOtherApplicantRelationship',
              ],
              conditional: {
                visible: {
                  field: 'representativeEvaluateWithOthers',
                  operator: 'equals',
                  value: 'yes',
                },
              },
            },
          ],
        },
        {
          id: 'agent-info',
          title: 'Representative Information',
          questionGroups: [
            {
              id: 'agent-details',
              format: 'default',
              questions: ['representativeAgentInfo'],
            },
          ],
        },
        {
          id: 'contact-info',
          title: 'Contact Information',
          questionGroups: [
            {
              id: 'office-contact',
              format: 'grid',
              questions: [
                'representativeContactName',
                'representativeContactPhone',
                'representativeContactEmail',
              ],
            },
          ],
        },
      ],
    },
    sharedOneAmericaSections.healthPreQuestionSection,
    sharedOneAmericaSections.medicalHealthInfoFirstProposedInsured,
    sharedOneAmericaSections.medicalHealthInfoSecondProposedInsured,
    sharedOneAmericaSections.usPatriotAct,
    sharedOneAmericaSections.authDisclosurePersonalInfo,
    sharedOneAmericaSections.ltcPremiumAnnualSingle,
    sharedOneAmericaSections.ltcPremiumAnnualSingleRider,
    sharedOneAmericaSections.rofVia1035Exchange,
    {
      id: 'replacement-notice',
      title: 'Important Notice Regarding Replacement Of Life Insurance Or Annuities',
      subsections: [
        /* Company Selection */
        {
          id: 'replacement-notice-company-selection',
          title: 'Company Selection',
          questionGroups: [
            {
              id: 'replacement-notice-company-selection-group',
              format: 'default',
              questions: ['replacementCompanySelection'],
            },
          ],
        },

        /* Existing Insurance / Annuities */
        {
          id: 'replacement-notice-existing-insurance',
          title: 'Existing Insurance/Annuities',
          questionGroups: [
            {
              id: 'replacement-notice-existing-insurance-group',
              format: 'default',
              questions: ['replacementExistingInsurance'],
            },
          ],
        },

        /* Replacement Questions */
        {
          id: 'replacement-notice-questions',
          title: 'Replacement Questions',
          questionGroups: [
            {
              id: 'replacement-notice-questions-group',
              format: 'default',
              questions: ['replacementDiscontinuingPolicy', 'replacementUsingFunds'],
            },
          ],
        },

        /* Existing Policy/Contract Table */
        {
          id: 'replacement-notice-existing-policies',
          title: 'Existing Policy or Contract Information',
          questionGroups: [
            {
              id: 'replacement-notice-existing-policies-group',
              format: 'default',
              questions: ['replacementExistingPolicies'],
            },
          ],
        },

        /* Replacement Reason */
        {
          id: 'replacement-notice-reason',
          title: 'Replacement Reason',
          questionGroups: [
            {
              id: 'replacement-notice-reason-group',
              format: 'default',
              questions: ['replacementReason'],
            },
          ],
        },

        /* Producer / Applicant Acknowledgments */
        {
          id: 'replacement-notice-producer',
          title: 'Producer / Applicant Acknowledgments',
          questionGroups: [
            {
              id: 'replacement-notice-confirmation-group',
              title:
                'ATTENTION CONSUMER, THIS NOTICE IS REQUIRED BY THE INSURANCE COMMISSIONER. PLEASE READ IT CAREFULLY BEFORE SIGNING.',
              description:
                'You have the right, within 30 days after delivery of a replacement policy or contract to return it to the company or its representative. In such case, this policy or contract will be void from the beginning. The Company will refund the premium or considerations paid on it including any policy fees or charges or, in the case of a variable or market value adjustment policy or contract, a payment of the cash surrender value provided under the policy or contract plus the fees and other charges deducted from the gross premiums or   considerations or imposed under such policy or contract.',
              format: 'grid',
              questions: [
                'replacementProducerName',
                'replacementProducerAddress',
                'replacementDoNotWantNotice',
                'replacementDoNotWantNoticeSecondaryInsured',
              ],
            },
          ],
        },
      ],
      conditional: {
        visible: {
          field: 'policyReplacement',
          operator: 'equals',
          value: 'yes',
        },
      },
    },
    sharedOneAmericaSections.electronicPremiumPaymentAuthorization,
    /* 23 ▸ LTC Insurance Personal Worksheet ------------------------------------------------------- */
    {
      id: 'ltc-insurance-personal-worksheet',
      title: 'Long-Term Care Insurance Personal Worksheet',
      subsections: [
        /* Premium Information */
        {
          id: 'ltc-worksheet-premium-info',
          title: 'Premium Information',
          questionGroups: [
            {
              id: 'ltc-worksheet-premium-info-group',
              format: 'default',
              questions: ['ltcPremiumPerMonth', 'ltcPremiumPerYear', 'ltcPremiumOneTime'],
            },
          ],
        },

        /* Questions About Your Income */
        {
          id: 'ltc-worksheet-income',
          title: 'Questions About Your Income',
          questionGroups: [
            {
              id: 'ltc-worksheet-income-resources',
              format: 'default',
              questions: ['ltcResourcesForPremium', 'ltcOtherResourceSpecify'],
            },
            {
              id: 'ltc-worksheet-income-affordability',
              format: 'default',
              questions: ['ltcAffordIfSpouseDies'],
            },
            {
              id: 'ltc-worksheet-income-overview',
              format: 'grid',
              questions: ['ltcHouseholdIncome', 'ltcIncomeChangeExpectation'],
            },
            {
              id: 'ltc-worksheet-income-impact',
              format: 'grid',
              questions: ['ltcIncomeChangeAffectPremium', 'ltcBuyInflationProtection'],
            },
            {
              id: 'ltc-worksheet-future-costs',
              format: 'default',
              questions: ['ltcPayForFutureCosts', 'ltcPayForFutureCostsOther'],
            },
            {
              id: 'ltc-worksheet-elimination-period',
              format: 'default',
              questions: ['ltcPayDuringElimination'],
            },
            {
              id: 'ltc-worksheet-assets',
              format: 'grid',
              questions: ['ltcAssetsWorth', 'ltcAssetsChangeExpectation'],
            },
            {
              id: 'ltc-worksheet-disclosure-group',
              format: 'default',
              questions: ['ltcDisclosureStatement', 'ltcAgreeReviewed'],
            },
            {
              id: 'ltc-worksheet-agent-ack',
              format: 'grid',
              questions: [
                'ltcAgentExplained',
                'ltcAgentPrintedName',
                'personalWorksheetNotSuitable',
              ],
            },
          ],
        },
      ],
    },
    sharedOneAmericaSections.preInterviewWorksheet,
    sharedOneAmericaSections.temporaryInsuranceAgreement,
    sharedOneAmericaSections.w4p,
    sharedOneAmericaSections.alcoholUseSupplement,
    /* 26 ▸ Certificate of Insurability and Application for Delivery ------------------------------------------------------- */
    {
      id: 'certificate-of-insurability',
      title: 'Certificate of Insurability and Application for Delivery',
      subsections: [
        /* Policy Information */
        {
          id: 'cert-delivery-policy-info',
          title:
            'Certificate of Insurability and Application for Delivery (Life and Health Insurance)',
          questionGroups: [
            {
              id: 'cert-delivery-policy-info-group',
              format: 'grid',
              questions: ['certDeliveryPolicyNumber', 'certDeliveryName'],
            },
            {
              id: 'cert-delivery-effective-date-group',
              format: 'default',
              questions: ['certDeliveryEffectiveDate'],
            },
            {
              id: 'cert-delivery-statements-group',
              title: 'Statements',
              description: `1. Except how amended, I represent that the responses provided on the application, as amended, including Part 1, Part 2 and Paramed Exam Form (if included) remain true and accurate.\n\n
2. I represent that since the earliest date of the application and the dates of all medical examinations required by the Company for this policy, no person proposed for insurance has:
    - been treated for or diagnosed by a member of the medical profession for any illness or personal injury of any kind;
    - been advised by a member of the medical profession to take any medication, or has begun taking any medication not previously disclosed on the application Part 1, Part 2 or Paramed Exam Form (if included);
    - been examined or evaluated by any physician or facility, nor is any exam or evaluation presently scheduled in the next 3 months;
    - applied for other life or health insurance, or for reinstatement which was declined or issued other than applied for, or had renewal refused.`,
              format: 'default',
              questions: ['certDeliveryStatementsTrue', 'certDeliveryNoResponseDetails'],
            },
          ],
        },
      ],
    },
    sharedOneAmericaSections.aviationSupplement,
    sharedOneAmericaSections.drugUseSupplement,
    sharedOneAmericaSections.armedForcesSupplement,
    sharedOneAmericaSections.avocationSupplement,
    sharedOneAmericaSections.foreignNationalsTravelSupplement,
    sharedOneAmericaSections.financialInstitutionDisclosure,
    sharedOneAmericaSections.assignment1035Exchange,
    sharedOneAmericaSections.acceptance1035Exchange,
    sharedOneAmericaSections.section1035PartialTransfer,
    sharedOneAmericaSections.powerOfAttorneyAffidavit,
    sharedOneAmericaSections.signatureAuthorityEntity,
  ],
  questions: {
    ...questions,
  },
};
